import type { Option } from '@ksyos/design-system';
import { Box, FormErrorMessage, FormParagraph, IconButton } from '@ksyos/design-system';
import { useState } from 'react';
import type { PaginatedListResponseBody } from '@common/interfaces/api/apiResponse';
import type { IOrganizationFilters, IOrganizationGeneralSettings } from '@common/validation/organization';
import type { IOrganization } from '@frontend/components/interfaces';
import type { OrganizationRelationship } from '@frontend/components/interfaces/organizationRelationship';
import type { ValidationErrors } from '@frontend/utils';
import { MatchType, OrganizationRelationshipType, OrganizationStructureType } from '@common/enums';
import { kvkNumberRegex } from '@common/utils/regularExpressions';
import { stringToUuid } from '@common/utils/uuid';
import { OrganizationSelection } from '@frontend/components/elements/OrganizationSelection';
import { EnumSelect } from '@frontend/components/fields/EnumSelect';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation, useAbortableFetch } from '@frontend/utils';
import type { OrganizationRelationshipAction } from './utils/organizationRelationshipsReducer';
import { OrganizationRelationshipsActions } from './utils/organizationRelationshipsReducer';

export type OrganizationRelationshipFormComponentProps<T extends IOrganization | IOrganizationGeneralSettings> = {
    dispatchRelationshipAction: React.Dispatch<OrganizationRelationshipAction<T>>;
    index: number;
    onRemoveValidationErrors?: (keys: string[]) => void;
    organization: IOrganization | IOrganizationGeneralSettings;
    organizationId?: string;
    organizationRelationship: OrganizationRelationship;
    relationshipsErrors?: ValidationErrors;
};

export function OrganizationRelationshipFormComponent<T extends IOrganization | IOrganizationGeneralSettings>(
    props: Readonly<OrganizationRelationshipFormComponentProps<T>>
) {
    const [defaultOptions, setDefaultOptions] = useState<Option[]>([]);

    const { index, organizationId, organizationRelationship, organization, dispatchRelationshipAction } = props;
    const { organizationStructureType } = organization;

    const isEditable = organizationRelationship.id === undefined;
    const language = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();

    if (organizationRelationship.isDeleted) {
        return null;
    }

    return (
        <Box
            display="flex"
            flexGrow={[1, null, null, null]}
            gap={2}
            my={0}
        >
            <Box mb={4}>
                {isEditable && (
                    <EnumSelect
                        enumObj={OrganizationRelationshipType}
                        labels={language.organizationRelationshipTypeEnum}
                        onChange={(item) => {
                            if (!item?.value) {
                                return;
                            }
                            const updatedRelationship = {
                                ...organizationRelationship,
                                type: item.value,
                            };
                            dispatchRelationshipAction({
                                payload: { index, relationship: updatedRelationship },
                                type: OrganizationRelationshipsActions.updateRelationship,
                            });
                        }}
                        size="medium"
                        value={organizationRelationship.type}
                    />
                )}
                {!isEditable && organizationRelationship.type && (
                    <FormParagraph size="medium">
                        {language.organizationRelationshipTypeEnum[organizationRelationship.type]}
                    </FormParagraph>
                )}
            </Box>
            <Box flexGrow={[1, null, null, null]}>
                {isEditable && (
                    <OrganizationSelection
                        defaultOptions={defaultOptions}
                        excludeOrganizationIds={organizationId ? [organizationId] : undefined}
                        isClearable={true}
                        matchType={MatchType.contains}
                        onChange={async (value) => {
                            if (!value) {
                                return;
                            }
                            const updatedRelationship = {
                                ...organizationRelationship,
                                relatedOrganizationId: stringToUuid(value),
                            };
                            dispatchRelationshipAction({
                                payload: { index, relationship: updatedRelationship },
                                type: OrganizationRelationshipsActions.updateRelationship,
                            });
                        }}
                        onMenuOpen={async () => {
                            await loadDefaultOptions();
                        }}
                        organizationRelationshipType={organizationRelationship.type}
                        organizationStructureType={organizationStructureType || OrganizationStructureType.organization}
                        value={organizationRelationship.relatedOrganizationId}
                    />
                )}
                {!isEditable && organizationRelationship.relatedOrganization?.name && (
                    <FormParagraph size="medium">{organizationRelationship.relatedOrganization.name}</FormParagraph>
                )}
                {getRelationshipErrorForIndex(index) && (
                    <FormErrorMessage
                        sx={{
                            marginBottom: 2,
                        }}
                    >
                        {getValidationErrorTranslation(getRelationshipErrorForIndex(index))}
                    </FormErrorMessage>
                )}
            </Box>
            <IconButton
                icon="delete"
                onClick={() => deleteRelationshipForIndex(index)}
            />
        </Box>
    );

    function getRelationshipErrorForIndex(relationshipIndex: number) {
        if (!props.relationshipsErrors) {
            return undefined;
        }

        const errorKey = props.relationshipsErrors
            .keySeq()
            .find((key) => (key ? key.startsWith(`organizationRelationships.${relationshipIndex}.`) : false));

        return errorKey ? props.relationshipsErrors.get(errorKey) : undefined;
    }

    function deleteRelationshipForIndex(relationshipIndex: number) {
        dispatchRelationshipAction({
            payload: relationshipIndex,
            type: OrganizationRelationshipsActions.removeRelationship,
        });

        removeErrorsForIndex(relationshipIndex);
    }

    function removeErrorsForIndex(relationshipIndex: number) {
        if (!props.relationshipsErrors) {
            return;
        }

        const errorKeysForIndex = props.relationshipsErrors
            .keySeq()
            .toArray()
            .filter((key) => key?.startsWith(`organizationRelationships.${relationshipIndex}.`) === true);

        if (errorKeysForIndex.length > 0) {
            props.onRemoveValidationErrors?.(errorKeysForIndex);
        }
    }

    async function loadDefaultOptions() {
        const filters = getFilters();
        const result = await abortableFetch.fetchJson<PaginatedListResponseBody<IOrganization>>(
            '/api/organizations/list',
            'POST',
            { filters }
        );
        const loadedOrganizations = result.json?.items;
        const options: Option[] = loadedOrganizations
            ? loadedOrganizations
                  .filter((loadedOrganization) => loadedOrganization.id !== organizationId)
                  .map((loadedOrganization) => ({
                      label: loadedOrganization.name,
                      value: loadedOrganization.id.toString(),
                  }))
            : [];
        setDefaultOptions(options);
    }

    function getFilters() {
        const filters: IOrganizationFilters = {};

        const hoedRelations: OrganizationRelationshipType[] = [
            OrganizationRelationshipType.isHoedOf,
            OrganizationRelationshipType.isPartOfHoed,
        ];
        const branchRelations: OrganizationRelationshipType[] = [
            OrganizationRelationshipType.isBranchOf,
            OrganizationRelationshipType.isParentOf,
        ];
        const duplicateRelations: OrganizationRelationshipType[] = [
            OrganizationRelationshipType.isDuplicateOf,
            OrganizationRelationshipType.isDuplicatedBy,
        ];

        if ([...hoedRelations, ...duplicateRelations].includes(organizationRelationship.type)) {
            filters.cityOrZip = organization.zip ?? undefined;
            filters.houseNumber = organization.houseNumber ?? undefined;
        }

        if ([...branchRelations, ...duplicateRelations].includes(organizationRelationship.type)) {
            filters.kvkNumber = organization.kvkNumber?.match(kvkNumberRegex) ? organization.kvkNumber : undefined;
            filters.nameStatutory = organization.nameStatutory ?? undefined;
        }

        if (organizationRelationship.type === OrganizationRelationshipType.isPartOfHoed) {
            filters.organizationStructureTypes = [
                OrganizationStructureType.hoed,
                OrganizationStructureType.organization,
            ];
        }

        if (organizationRelationship.type === OrganizationRelationshipType.isBranchOf) {
            filters.organizationStructureTypes = [
                OrganizationStructureType.branch,
                OrganizationStructureType.organization,
            ];
        }

        return filters;
    }
}
